import { reactive } from 'vue'

import { AxiosError, type AxiosInstance } from 'axios'

/** API faults set from the DevDrawer. In memory only, so a reload always clears them. */
export const devApiFaults = reactive({
  /** Every request fails with a network error before it is sent. */
  offline: false,
  /** Delay added before each request is sent. */
  latencyMs: 0,
  /** The next response that succeeds is discarded and surfaces as a network error. */
  dropNextResponse: false
})

export function hasActiveDevApiFaults(): boolean {
  return devApiFaults.offline || devApiFaults.latencyMs > 0 || devApiFaults.dropNextResponse
}

/**
 * Installs the fault interceptors. Call before `axiosRetry` and the error-reporting interceptor:
 * request interceptors run in reverse order of registration and response interceptors in order, so
 * an injected failure then goes through retry and reporting the way a real network failure does.
 */
export function installDevApiFaults(instance: AxiosInstance): void {
  instance.interceptors.request.use(async (config) => {
    if (devApiFaults.latencyMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, devApiFaults.latencyMs))
    }
    if (devApiFaults.offline) {
      throw new AxiosError('Network Error (DevDrawer: offline)', AxiosError.ERR_NETWORK, config)
    }
    return config
  })

  instance.interceptors.response.use((response) => {
    if (!devApiFaults.dropNextResponse) return response

    devApiFaults.dropNextResponse = false
    throw new AxiosError(
      'Network Error (DevDrawer: response dropped)',
      AxiosError.ERR_NETWORK,
      response.config,
      response.request
    )
  })
}
